import { LedgerNoteEditableCell, TEditableNoteField } from "@/features/management-ledger/components/LedgerNoteEditableCell";
import { LedgerJobChangeBanPeriodCell } from "@/features/management-ledger/components/LedgerJobChangeBanPeriodCell";
import {
  LedgerEmploymentPeriodEditableCell,
  TUpdateEmploymentPeriodDraft,
} from "@/features/management-ledger/components/LedgerEmploymentPeriodEditableCell";
import { TJobLedgerRow } from "@/features/management-ledger/types/management-ledger";
import { Box } from "@mui/material";
import { MRT_ColumnDef } from "material-react-table";
export const JOB_LEDGER_PINNED_COLUMN_ID = "jobManagementNo";
const multiLineHeadProps = {
  className: "ledger-multiline-head",
  sx: { "& .Mui-TableHeadCell-Content-Wrapper": { whiteSpace: "pre-line" as const } },
};
const subColumnHeadProps = { className: "ledger-sub-col", sx: { verticalAlign: "middle !important" } };
const leafColumnHeadProps = { className: "ledger-leaf-col", sx: { verticalAlign: "middle !important" } };
export type TLedgerDraftEdits = Record<number, Partial<Record<TEditableNoteField, string>>>;
export type TUpdateLedgerDraft = (caseMatchId: number, field: TEditableNoteField, value: string) => void;
export const resolveDraftValue = (draftEdits: TLedgerDraftEdits, caseMatchId: number | null, field: TEditableNoteField, original: string): string =>
  (caseMatchId != null ? draftEdits[caseMatchId]?.[field] : undefined) ?? original;
export type TEmploymentPeriodDrafts = Record<number, number | null>;
const jobCellMergeProps = {
  muiTableBodyCellProps: ({
    row,
  }: {
    row: {
      original: TJobLedgerRow;
    };
  }) => (row.original.jobRowSpan === 0 ? { sx: { display: "none" } } : { rowSpan: row.original.jobRowSpan, sx: { verticalAlign: "top" as const } }),
};
export const getJobLedgerColumns = (
  draftEdits: TLedgerDraftEdits,
  updateDraft: TUpdateLedgerDraft,
  isEditing: boolean,
  jobEmploymentPeriodDrafts: TEmploymentPeriodDrafts,
  updateJobEmploymentPeriodDraft: TUpdateEmploymentPeriodDraft,
  referralEmploymentPeriodDrafts: TEmploymentPeriodDrafts,
  updateReferralEmploymentPeriodDraft: TUpdateEmploymentPeriodDraft,
): MRT_ColumnDef<TJobLedgerRow>[] => [
  { id: JOB_LEDGER_PINNED_COLUMN_ID, accessorKey: "jobManagementNo", header: "求人管理番号", size: 116, ...jobCellMergeProps },
  {
    accessorKey: "employerInfo",
    header: "①求人者の氏名又は名称\n②所在地\n③連絡担当者／連絡先電話番号",
    size: 228,
    muiTableHeadCellProps: multiLineHeadProps,
    ...jobCellMergeProps,
    Cell: ({ cell }) => <Box sx={{ whiteSpace: "pre-line" }}>{cell.getValue<string>()}</Box>,
  },
  { accessorKey: "receptionDate", header: "④受付年月日", size: 116, ...jobCellMergeProps },
  { accessorKey: "validPeriod", header: "⑤有効期間", size: 106, ...jobCellMergeProps },
  { accessorKey: "openings", header: "⑥求人数", size: 88, ...jobCellMergeProps },
  { accessorKey: "occupation", header: "⑦職種", size: 130, ...jobCellMergeProps },
  { accessorKey: "workplace", header: "⑧就業場所", size: 172, ...jobCellMergeProps },
  {
    accessorKey: "employmentPeriod",
    header: "⑨雇用期間",
    size: 102,
    ...jobCellMergeProps,
    Cell: ({ row }) => (
      <LedgerEmploymentPeriodEditableCell
        entityId={row.original.jobId}
        value={jobEmploymentPeriodDrafts[row.original.jobId] ?? row.original.employmentPeriod}
        isEditing={isEditing}
        onChange={updateJobEmploymentPeriodDraft}
      />
    ),
  },
  { accessorKey: "wage", header: "⑩賃金", size: 109, ...jobCellMergeProps },
  {
    id: "referralStatus",
    header: "⑪職業紹介の取扱状況",
    columns: [
      { accessorKey: "jobName", header: "求人名", size: 164, muiTableHeadCellProps: subColumnHeadProps },
      { accessorKey: "referralDate", header: "紹介年月日", size: 106, muiTableHeadCellProps: subColumnHeadProps },
      {
        accessorKey: "jobseekerName",
        header: "求職者氏名",
        size: 220,
        muiTableHeadCellProps: { ...subColumnHeadProps, sx: { ...subColumnHeadProps.sx, width: 220, maxWidth: 220 } },
        muiTableBodyCellProps: { sx: { width: 220, maxWidth: 220, whiteSpace: "normal", wordBreak: "break-word" } },
      },
      { accessorKey: "hiringResult", header: "採否結果", size: 88, muiTableHeadCellProps: subColumnHeadProps },
      { accessorKey: "hiredDate", header: "採用年月日", size: 106, muiTableHeadCellProps: subColumnHeadProps },
      {
        accessorKey: "referralEmploymentPeriod",
        header: "雇用期間",
        size: 180,
        muiTableHeadCellProps: { ...subColumnHeadProps, sx: { ...subColumnHeadProps.sx, width: 180, maxWidth: 180 } },
        muiTableBodyCellProps: { sx: { width: 180, maxWidth: 180 } },
        Cell: ({ row }) =>
          row.original.caseMatchId == null ? null : (
            <LedgerEmploymentPeriodEditableCell
              entityId={row.original.caseMatchId}
              value={referralEmploymentPeriodDrafts[row.original.caseMatchId] ?? row.original.referralEmploymentPeriod}
              isEditing={isEditing}
              onChange={updateReferralEmploymentPeriodDraft}
            />
          ),
      },
      {
        id: "permanentEmploymentGroup",
        header: "無期雇用就職者に関する事項等",
        columns: [
          {
            accessorKey: "permanentEmploymentNote",
            header: "転職勧奨禁止期間",
            size: 170,
            muiTableHeadCellProps: leafColumnHeadProps,
            muiTableBodyCellProps: { sx: { whiteSpace: "normal", wordBreak: "break-word" } },
            Cell: ({ row }) => (
              <LedgerJobChangeBanPeriodCell
                row={row.original}
                value={resolveDraftValue(draftEdits, row.original.caseMatchId, "permanentEmploymentNote", row.original.permanentEmploymentNote)}
                isEditing={isEditing}
                onChange={updateDraft}
              />
            ),
          },
          {
            accessorKey: "separationStatusNote",
            header: "6か月以内の離職状況",
            size: 228,
            muiTableHeadCellProps: leafColumnHeadProps,
            muiTableBodyCellProps: { sx: { whiteSpace: "normal", wordBreak: "break-word" } },
            Cell: ({ row }) => (
              <LedgerNoteEditableCell
                row={row.original}
                field="separationStatusNote"
                value={resolveDraftValue(draftEdits, row.original.caseMatchId, "separationStatusNote", row.original.separationStatusNote)}
                isEditing={isEditing}
                onChange={updateDraft}
              />
            ),
          },
        ],
      },
      {
        accessorKey: "remarks",
        header: "備考",
        size: 200,
        muiTableHeadCellProps: subColumnHeadProps,
        muiTableBodyCellProps: { sx: { whiteSpace: "normal", wordBreak: "break-word" } },
        Cell: ({ row }) => (
          <LedgerNoteEditableCell
            row={row.original}
            field="remarks"
            value={resolveDraftValue(draftEdits, row.original.caseMatchId, "remarks", row.original.remarks)}
            isEditing={isEditing}
            onChange={updateDraft}
          />
        ),
      },
    ],
  },
];
